/**
 * Resolves a CEPI Agents evidence deep link to a real viewer.
 *
 * The backend mints `/agents/evidence/<corpus>[/drug-<id>][/doc-<id>]`
 * (`backend/app/agents/tools/__init__.py:_deep_link`), whose own docstring
 * says: "Task 11 (frontend) owns the real route; this is a placeholder that is
 * never blank, per §4.5's requirement that every reference carry a deep link."
 * That route was never added, so every citation in a report fell through to
 * App.tsx's `path="*"` catch-all and 404'd — reported from staging.
 *
 * This owns the mapping. Where a corpus has no viewer yet it says so plainly,
 * with the identifiers visible, rather than dead-ending on a 404: an
 * unviewable citation is still a citation the user may need to chase by hand.
 */
import { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const PREFIX = '/agents/evidence/';

export interface ParsedEvidenceLink {
  corpus: string;
  drugId: number | null;
  documentId: number | null;
  drugVersionId: number | null;
  historicalSection: string | null;
  invalidHistoricalTarget: boolean;
}

/** Exported for tests: parse the deep-link path the backend mints. */
export function parseEvidencePath(pathname: string): ParsedEvidenceLink | null {
  const [path = '', query = ''] = pathname.split('?', 2);
  if (!path.startsWith(PREFIX)) return null;
  const parts = path.slice(PREFIX.length).split('/').filter(Boolean);
  if (parts.length === 0) return null;

  const corpus = parts[0];
  if (!corpus) return null;
  const rest = parts.slice(1);
  let drugId: number | null = null;
  let documentId: number | null = null;
  for (const part of rest) {
    const drug = /^drug-(\d+)$/.exec(part);
    if (drug) drugId = Number(drug[1]);
    const doc = /^doc-(\d+)$/.exec(part);
    if (doc) documentId = Number(doc[1]);
  }
  const params = new URLSearchParams(query);
  const historical = params.get('source') === 'historical';
  const rawVersion = params.get('version');
  const parsedVersion = rawVersion !== null && /^\d+$/.test(rawVersion)
    ? Number(rawVersion) : null;
  const drugVersionId = historical && parsedVersion !== null && parsedVersion > 0
    ? parsedVersion : null;
  const historicalSection = historical && params.get('hsection')?.trim()
    ? params.get('hsection')!.trim() : null;
  return {
    corpus, drugId, documentId, drugVersionId, historicalSection,
    invalidHistoricalTarget: historical && (
      corpus !== 'us_label' || drugId === null || drugVersionId === null
    ),
  };
}

/**
 * Exported for tests. Returns the app-relative target, or null when this
 * corpus has no viewer.
 *
 * Only routes that ACTUALLY EXIST in App.tsx are mapped — guessing a plausible
 * path would swap a 404 for a subtly wrong document, which is worse for a
 * citation whose whole purpose is provenance.
 */
export function resolveEvidenceTarget(link: ParsedEvidenceLink): string | null {
  const { corpus, drugId, documentId, drugVersionId, historicalSection } = link;
  if (link.invalidHistoricalTarget) return null;
  if (corpus === 'sba' && documentId !== null) {
    return `/sba/documents/${documentId}/viewer`;
  }
  if (corpus === 'fda_letter' && documentId !== null) {
    return `/fda-letter/${documentId}`;
  }
  // us_label: the drug page IS the per-document viewer (current label with no
  // version, or a specific historical version) — no document identity is
  // lost by routing there.
  if (corpus === 'us_label' && drugId !== null && drugVersionId !== null) {
    const params = new URLSearchParams({
      source: 'historical', version: String(drugVersionId),
    });
    if (historicalSection) params.set('hsection', historicalSection);
    return `/drug/${drugId}?${params.toString()}`;
  }
  if (corpus === 'us_label' && drugId !== null) {
    return `/drug/${drugId}`;
  }
  // Drugs@FDA label: the drug page opens the exact label via
  // `source=fdalabelpdfdoc&version=<fda_label_supplements.id>`.
  if (corpus === 'fda_label_pdf' && drugId !== null && documentId !== null) {
    const params = new URLSearchParams({
      source: 'fdalabelpdfdoc', version: String(documentId),
    });
    return `/drug/${drugId}?${params.toString()}`;
  }
  // Rule 14 / controller ruling R-A: a citation carrying a document id for a
  // corpus with no dedicated document viewer must not silently lose that
  // document by falling through to the drug-only page — e.g. an
  // `fda_approval_letter` citation for drug 94462 / document 7961 is a
  // distinct document from whatever `/drug/94462` currently shows. Render the
  // explicit no-viewer page instead, which keeps every identifier visible.
  if (documentId !== null) return null;
  // A corpus identified by drug alone (no document id, so nothing is lost):
  // the drug page remains the canonical fallback.
  if (drugId !== null) return `/drug/${drugId}`;
  return null;
}

export default function AgentEvidencePage() {
  const { pathname, search } = useLocation();
  const link = useMemo(() => parseEvidencePath(`${pathname}${search}`), [pathname, search]);
  const target = useMemo(() => (link ? resolveEvidenceTarget(link) : null), [link]);

  if (link && target) return <Navigate to={target} replace />;

  return (
    <div className="mx-auto max-w-2xl px-6 py-16" data-testid="evidence-no-viewer">
      <h1 className="text-xl font-semibold text-slate-900">
        No viewer for this source yet
      </h1>
      <p className="mt-3 text-sm text-slate-600">
        This citation points at a document type that does not have an in-app
        viewer. The reference is still valid — these identifiers locate it:
      </p>
      <dl className="mt-4 rounded border border-slate-200 bg-slate-50 p-4 text-sm">
        <div className="flex gap-2">
          <dt className="w-28 text-slate-500">Source</dt>
          <dd className="font-mono text-slate-800" data-testid="evidence-corpus">
            {link?.corpus ?? 'unrecognised link'}
          </dd>
        </div>
        {link?.drugId != null && (
          <div className="mt-1 flex gap-2">
            <dt className="w-28 text-slate-500">Drug</dt>
            <dd className="font-mono text-slate-800">{link.drugId}</dd>
          </div>
        )}
        {link?.documentId != null && (
          <div className="mt-1 flex gap-2">
            <dt className="w-28 text-slate-500">Document</dt>
            <dd className="font-mono text-slate-800">{link.documentId}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
